import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { resolvePath } from "./definition";
import {
  columnHeader,
  formatCell,
  formatListItems,
  formatSummaryCell,
  pathLabel,
  summaryHeader,
  type Translate,
} from "./labels";
import type { Catalog, ReportResult, ReportSpec, ResultColumn } from "./types";

const XLSX_TYPE = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

export function reportFileName(name: string, today: Date = new Date()): string {
  const pad = (value: number) => String(value).padStart(2, "0");
  const slug = name
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60) || "rapport";
  return `${slug}-${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}.xlsx`;
}

export type ExportColumn = { column: ResultColumn; header: string };

/** Headers in the order of the result, with the user's own column names. */
export function exportColumns(t: Translate, catalog: Catalog, definition: ReportSpec, result: ReportResult): ExportColumn[] {
  return result.columns.map((column) => {
    if (column.display === "GROUP" || column.display === "MEASURE") {
      return { column, header: summaryHeader(t, catalog, definition, column) };
    }
    const spec = definition.columns.find((item) => item.path === column.path);
    const resolved = column.path ? resolvePath(catalog, definition.source, column.path) : null;
    return {
      column,
      header: column.label || (spec ? columnHeader(t, catalog, definition.source, spec)
        : pathLabel(t, resolved, column.path ?? column.key)),
    };
  });
}

/** The text of a cell, as on screen (tables and summaries). A list has one line per related row. */
export function cellText(t: Translate, catalog: Catalog, definition: ReportSpec, column: ResultColumn,
                         row: Record<string, unknown>): string {
  const field = column.path ? resolvePath(catalog, definition.source, column.path)?.field ?? null : null;
  if (column.display === "LIST") {
    const items = formatListItems(t, column.type, field, row[column.key], Number(row[`${column.key}__more`] ?? 0));
    return items.length ? items.join("\n") : "—";
  }
  if (column.display === "GROUP" || column.display === "MEASURE") {
    return formatSummaryCell(t, column, column.display === "GROUP" ? field : null, row[column.key]);
  }
  return formatCell(t, column.type, column.display, field, row[column.key], Number(row[`${column.key}__more`] ?? 0));
}

/**
 * A cell value for Excel: real numbers and dates where possible so they sort and filter in Excel;
 * everything else as the same text the screen shows.
 */
export function excelValue(t: Translate, catalog: Catalog, definition: ReportSpec, column: ResultColumn,
                           row: Record<string, unknown>): string | number | Date | null {
  const value = row[column.key];
  if (value === null || value === undefined) return null;
  if (column.display === "COUNT" || column.display === "MEASURE") return Number(value);
  if (column.display === "GROUP" && !column.bucket && column.type === "NUMBER" && typeof value === "number") return value;
  if (column.display === "VALUE" && column.type === "NUMBER" && typeof value === "number") return value;
  if (column.display === "VALUE" && column.type === "DATE" && typeof value === "string") {
    const [year, month, day] = value.split("-").map(Number);
    if (year && month && day) return new Date(Date.UTC(year, month - 1, day));
  }
  const text = cellText(t, catalog, definition, column, row);
  return text === "—" ? null : text;
}

export function buildReportWorkbook(t: Translate, catalog: Catalog, definition: ReportSpec, result: ReportResult,
                                    sheetName: string): ExcelJS.Workbook {
  const workbook = new ExcelJS.Workbook();
  workbook.created = new Date();
  const sheet = workbook.addWorksheet(sheetName.replace(/[\\/?*[\]:]/g, " ").slice(0, 31).trim() || "Rapport", {
    views: [{ state: "frozen", ySplit: 1 }],
  });
  const columns = exportColumns(t, catalog, definition, result);
  sheet.columns = columns.map(({ column, header }) => ({
    header,
    key: column.key,
    width: 14,
    style: column.type === "DATE" && column.display === "VALUE" ? { numFmt: "dd-mm-yyyy" } : undefined,
  }));
  result.rows.forEach((row) => {
    const added = sheet.addRow(Object.fromEntries(columns.map(({ column }) => [column.key, excelValue(t, catalog, definition, column, row)])));
    // Lists have one line per related row; keep the lines of a row aligned across columns. Set the row first:
    // a row alignment replaces the alignment of all its cells.
    added.alignment = { vertical: "top" };
    columns.forEach(({ column }, index) => {
      if (column.display === "LIST") added.getCell(index + 1).alignment = { wrapText: true, vertical: "top" };
    });
  });
  const header = sheet.getRow(1);
  header.font = { bold: true };
  header.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFE0E7FF" } };
  if (columns.length > 0) {
    sheet.autoFilter = { from: { row: 1, column: 1 }, to: { row: 1, column: columns.length } };
  }
  sheet.columns.forEach((column) => {
    let longest = String(column.header ?? "").length;
    column.eachCell?.({ includeEmpty: false }, (cell) => {
      const text = cell.value instanceof Date ? "dd-mm-yyyy" : String(cell.value ?? "");
      text.split("\n").forEach((line) => { longest = Math.max(longest, line.length); });
    });
    column.width = Math.min(50, Math.max(10, longest + 2));
  });
  return workbook;
}

export async function downloadReportExcel(t: Translate, catalog: Catalog, definition: ReportSpec, result: ReportResult,
                                          name: string): Promise<void> {
  const workbook = buildReportWorkbook(t, catalog, definition, result, name);
  const buffer = await workbook.xlsx.writeBuffer();
  saveAs(new Blob([buffer], { type: XLSX_TYPE }), reportFileName(name));
}
